import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Mail, Eye, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, EmptyState } from "../../components/common/Primitives";
import DigestShareCard from "../../components/startup/DigestShareCard";
import api from "../../lib/api";

export default function InvestorDigestPreview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/startup/investor-digest-preview");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preview");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const fmtAsk = (n) => {
    if (!n) return "—";
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)} Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
    return `₹${n}`;
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="space-y-5">
        <div>
          <Link to="/startup/dashboard" className="text-xs text-ink-soft hover:text-gold flex items-center gap-1" data-testid="digest-preview-back">
            <ArrowLeft size={12} /> Dashboard
          </Link>
          <h1 className="font-display text-3xl text-ink mt-1 flex items-center gap-2">
            <Mail size={22} className="text-gold" /> Investor Digest Preview
          </h1>
          <div className="text-xs text-ink-soft mt-1 max-w-2xl">
            This is exactly how your startup appears in an investor's weekly digest email. Preview any time before it goes out — Fridays 08:00 UTC.
          </div>
        </div>

        {loading || !data ? (
          <Card testId="digest-preview-loading">
            <div className="text-sm text-ink-soft italic">Loading preview…</div>
          </Card>
        ) : (
          <>
            <Card testId="digest-preview-stats">
              <div className="overline mb-3">Snapshot investors will see this week</div>
              <div className="grid sm:grid-cols-4 gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-soft">Trust Score</div>
                  <div className="font-heading text-2xl text-ink mt-1" data-testid="digest-trust-score">{data.trust_score}/100</div>
                  {data.trust_label && <div className="text-xs text-ink-soft mt-0.5">{data.trust_label}</div>}
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-soft">7-day delta</div>
                  <div
                    className={`font-heading text-2xl mt-1 ${data.trust_delta_7d > 0 ? "text-emerald-700" : data.trust_delta_7d < 0 ? "text-rose-700" : "text-ink"}`}
                    data-testid="digest-trust-delta"
                  >
                    {data.trust_delta_7d > 0 ? "▲" : data.trust_delta_7d < 0 ? "▼" : "—"} {Math.abs(data.trust_delta_7d)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-soft">Watching you</div>
                  <div className="font-heading text-2xl text-ink mt-1" data-testid="digest-watchers">{data.watchers}</div>
                  <div className="text-xs text-ink-soft mt-0.5">investors</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-soft">Funding ask</div>
                  <div className="font-heading text-2xl text-ink mt-1">{fmtAsk(data.funding_ask)}</div>
                </div>
              </div>
              {data.verified_pills.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5" data-testid="digest-pills">
                  {data.verified_pills.map((p) => (
                    <Pill key={p} tone="success">{p}</Pill>
                  ))}
                </div>
              )}
              <div className="mt-3 text-xs text-[#8A6600] italic" data-testid="digest-cta-hint">{data.cta_hint}</div>
            </Card>

            <DigestShareCard data={data} />

            <Card testId="digest-preview-html">
              <div className="flex items-center justify-between mb-3">
                <div className="overline flex items-center gap-1"><Eye size={12} /> Rendered email preview</div>
                <Button variant="secondary" onClick={load} testId="digest-refresh">Refresh</Button>
              </div>
              <div className="border border-navy/15 rounded overflow-hidden" data-testid="digest-html-frame">
                <iframe
                  title="Investor Digest Preview"
                  srcDoc={data.html_preview}
                  className="w-full min-h-[520px] border-0 bg-white"
                />
              </div>
            </Card>

            {data.watchers === 0 && (
              <EmptyState
                title="No investors watching yet"
                hint="Your digest slot activates as soon as an investor watchlists you. Publish a strong pitch deck to earn watchlist adds."
              />
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
